import type { PlaceHit } from './api';
import { found, type StopDraft } from './stops';

/**
 * What the search box shows: answers only for the text typed now (until typing settles, older answers are neither
 * shown nor picked), and whether it is still searching: waiting for typing to settle, or for the answer
 */
export function currentSearch<T>(asked: string, typed: string, answer: T | undefined, failed: boolean, fetching = false): { answer: T | undefined; failed: boolean; searching: boolean } {
  const text = typed.trim();
  const fresh = asked === text;
  return { answer: fresh ? answer : undefined, failed: fresh && failed, searching: text.length >= 3 && (!fresh || fetching) };
}

/** Escape in a place box with its list open closes the list, not the dialog around it (Radix hears Escape first, on the document) */
export function keepDialogOnEscape(event: { target: EventTarget | null; preventDefault: () => void }): void {
  const target = event.target as { getAttribute?: (name: string) => string | null } | null;
  if (target?.getAttribute?.('role') === 'combobox' && target.getAttribute('aria-expanded') === 'true') event.preventDefault();
}

/** A route line's look: the chosen one solid, the other dashed and muted. A click on it chooses it and must not reach the map, where it would place a stop */
export const lineOptions = (chosen: boolean) =>
  chosen
    ? { className: 'stroke-primary', weight: 5, opacity: 0.9, dashArray: undefined, bubblingMouseEvents: false }
    : { className: 'stroke-muted-foreground', weight: 4, opacity: 0.7, dashArray: '6 8', bubblingMouseEvents: false };

/** `make` once per distinct arguments: react-leaflet resets a marker whose icon or position is a new object, and a reset ends a drag in progress */
export function madeOnce<A extends Array<string | number | boolean>, T>(make: (...args: A) => T): (...args: A) => T {
  const made = new Map<string, T>();
  return (...args) => {
    const key = args.join('|');
    if (!made.has(key)) made.set(key, make(...args));
    return made.get(key)!;
  };
}

/** The row whose suggestions the map shows, and the highlighted one; null when none */
export type Browsing = { row: number; active: number } | null;

const NONE: PlaceHit[] = [];

/** The suggestions to show on the map: only while a row without a place is searched and its answer is ok */
export function mapMatches(browsing: Browsing, drafts: StopDraft[], answer: { status: string; places: PlaceHit[] } | undefined): PlaceHit[] {
  const row = browsing ? drafts[browsing.row] : undefined;
  return row && !found(row) && answer?.status === 'ok' ? answer.places : NONE;
}

/** The stop a map click fills: the first without a place; -1 once every stop has one */
export const nextToPlace = (drafts: StopDraft[]): number => drafts.findIndex((draft) => !found(draft));

/** A match's letter on the map and in the list: A, B, C… */
export const letterOf = (index: number): string => String.fromCharCode(65 + index);

/** The searched row after the panel changes: kept while only its own text changes; rows added, removed, moved or edited elsewhere drop it, so the matches never move to another row */
export function browsingAfter(browsing: Browsing, before: StopDraft[], after: StopDraft[]): Browsing {
  if (!browsing) return null;
  const same = before.length === after.length && after.every((row, at) => at === browsing.row || row === before[at]);
  return same ? browsing : null;
}

/** A press in the map's panel keeps the matches only on a search box or its list; anything else (a card, a kind, ↑ ↓ ✕) takes them off */
export const pressKeepsMatches = (target: { closest?: (selector: string) => unknown } | null): boolean => !!target?.closest?.('[role="combobox"], [role="listbox"]');

/** Escape in the map modal: takes the matches off the map (true), or closes an open list; only with neither does it close the modal */
export function escapeInMap(event: { target: EventTarget | null; preventDefault: () => void }, showing: boolean): boolean {
  if (!showing) {
    keepDialogOnEscape(event);
    return false;
  }
  event.preventDefault();
  return true;
}

